import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { BiSolidCoupon } from "react-icons/bi";
import { FaSearch, FaSort } from "react-icons/fa";
import { IoFilter } from "react-icons/io5";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getCoupons, deActivateCoupon, reActivate, deleteCoupon, resolveMarketplaceEnabled, isBranchMarketplaceEnabled } from "../../services/ClientService";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FaTrashAlt } from "react-icons/fa";
import ConfirmModal from "../ConfirmModal";
import { toast } from "react-toastify";
import { adminFetchJson } from "../../services/apiService";

const Coupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [enabledTabs, setEnabledTabs] = useState({ online: true, offline: true, marketplace: false });

    // New states for search, filters, date range and popup toggles
    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({ isActive: [true, false] });
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [showSortPopup, setShowSortPopup] = useState(false);
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [accordionOpen, setAccordionOpen] = useState({});
    const [activeTab, setActiveTab] = useState(null); // Will be set after fetching branch data
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [couponToDelete, setCouponToDelete] = useState(null);

    // Fetch branch data to check enabled store types
    useEffect(() => {
        const fetchBranchData = async () => {
            try {
                const userData = JSON.parse(localStorage.getItem("userData"));
                if (!userData) return;

                const { data } = await adminFetchJson('/clients/getBranchNamesForUser', {
                    method: 'POST',
                    body: {},
                });
                if (data.success && data.data) {
                    // For role 1: use selectedBranch, for role 2: use their own _id
                    const branchIdToCheck = userData.role === 1 
                        ? userData.selectedBranch 
                        : userData._id;
                    
                    const selectedBranch = data.data.find(
                        (b) => userData.role === 1 
                            ? b.masterId === branchIdToCheck 
                            : b._id === branchIdToCheck
                    );
                    
                    if (selectedBranch) {
                        const masterBranch = data.data.find((b) => b.masterAccount === true);
                        let marketplaceEnabled = resolveMarketplaceEnabled(selectedBranch, masterBranch);
                        // Role-2 logins may only receive their own branch — fetch master flag when needed
                        if (!selectedBranch.masterAccount && !masterBranch) {
                            marketplaceEnabled = await isBranchMarketplaceEnabled(selectedBranch._id);
                        }

                        // Set enabled tabs based on branch settings
                        const enabled = {
                            online: selectedBranch.enableOnlineStore === true,
                            offline: selectedBranch.enableOfflineStore === true,
                            marketplace: marketplaceEnabled,
                        };
                        setEnabledTabs(enabled);

                        // Set default active tab to the first enabled store type
                        if (!activeTab) {
                            if (enabled.online) setActiveTab('online');
                            else if (enabled.offline) setActiveTab('offline');
                            else if (enabled.marketplace) setActiveTab('marketplace');
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching branch data:", error);
            }
        };

        fetchBranchData();
        // Mount-only: resolve tabs from branch settings once; activeTab is intentionally read as initial value.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // If marketplace is disabled for this branch, fall back to an available tab
    useEffect(() => {
        if (activeTab === 'marketplace' && !enabledTabs.marketplace) {
            if (enabledTabs.online) setActiveTab('online');
            else if (enabledTabs.offline) setActiveTab('offline');
        }
    }, [enabledTabs.marketplace, enabledTabs.online, enabledTabs.offline, activeTab]);

    const handleCreateCoupon = () => {
        navigate(`/createCoupon`);
    };

    const handleEditCoupon = (item) => {
        navigate(`/editCoupon/${item._id}`);
    };

    const handleToggleCouponStatus = async (item) => {
        try {
            let { _id, isActive, id } = { ...item };
            const data = await (isActive ? deActivateCoupon({ _id }) : reActivate({ _id }));
            if (data.success) {
                setCoupons(PreviousCoupon =>
                    PreviousCoupon.map(c => c._id === _id ? { ...c, isActive: !isActive } : c)
                );
                console.log(`coupon ${id} ${isActive ? 'deActivated' : 'reActivated'} successfully`);
            } else {
                console.error(`Failed to toggle coupon status:`, data.message);
                toast.error(`Failed to toggle coupon status: ${data.message}`);
            }
        } catch (error) {
            console.error(`Error toggling coupon status:`, error);
            toast.error("Error toggling coupon status. Please check console.");
        }
    };

    // Memoized API call function using useCallback
    const getCouponsMethod = useCallback(async (params) => {
        setLoading(true);
        try {
            const data = await getCoupons(params);
            console.log(data, 190);
            setCoupons(data.success ? data.data : []);
        } catch (error) {
            console.error("Error fetching coupons:", error);
            setCoupons([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // New effect to update filters only when both start and end dates are selected
    useEffect(() => {
        if (startDate && endDate) {
            setFilters(prev => ({ ...prev, from: startDate, to: endDate }));
            setShowFilterPopup(false);
        }
    }, [startDate, endDate]);

    // Debounced search and filters update
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            const params = { searchTerm, ...filters };
            // Add storeType filter based on active tab
            if (activeTab) {
                params.storeType = activeTab;
            }
            getCouponsMethod(params);
        }, 800);
        return () => clearTimeout(debounceTimer);
    }, [searchTerm, filters, activeTab, getCouponsMethod]);

    const handleClearDateFilter = () => {
        setDateRange([null, null]);
        setFilters(prev => {
            const newFilters = { ...prev };
            delete newFilters.from;
            delete newFilters.to;
            return newFilters;
        });
    };

    const handleDeleteRequest = (coupon) => {
        setCouponToDelete(coupon);
        setConfirmModalVisible(true);
    };

    const confirmDeleteCoupon = async () => {
        try {
            const data = await deleteCoupon({ _id: couponToDelete._id });
            if (data.success) {
                setCoupons(prevCoupons => prevCoupons.filter(c => c._id !== couponToDelete._id));
                console.log(`Coupon ${couponToDelete.id} deleted successfully`);
            } else {
                console.error(`Failed to delete coupon:`, data.message);
                toast.error(`Failed to delete coupon: ${data.message}`);
            }
        } catch (error) {
            console.error(`Error deleting coupon:`, error);
            toast.error("Error deleting coupon. Please check console.");
        } finally {
            setConfirmModalVisible(false);
            setCouponToDelete(null);
        }
    };

    const cancelDeleteCoupon = () => {
        setConfirmModalVisible(false);
        setCouponToDelete(null);
    };


    return (
        <div className="h-screen overflow-hidden max-h-fit bg-white relative">
            <div className="bg-white p-6 sticky top-0 z-10 space-y-4">
                <div className="flex justify-between items-center">
                    <h1 className="text-3xl font-bold">Coupons</h1>
                    <button
                        onClick={handleCreateCoupon}
                        className="relative text-white font-bold bg-gradient-to-r from-blue-500 to-purple-500 border-2 border-transparent rounded-full px-5 py-2.5 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl group"
                    >
                        <span className="absolute inset-0 rounded-full border-2 border-blue-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"></span>
                        <span className="relative z-10 flex items-center">
                            <BiSolidCoupon className="mr-2 text-2xl" />
                            Create Coupon
                        </span>
                    </button>
                </div>

                {/* Tabs for Store Type */}
                <div className="flex gap-2 border-b border-gray-200">
                    {enabledTabs.online && (
                        <button
                            onClick={() => setActiveTab('online')}
                            className={`px-6 py-3 font-semibold transition-all duration-200 border-b-2 ${
                                activeTab === 'online'
                                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                        >
                            Online Coupons
                        </button>
                    )}
                    {enabledTabs.offline && (
                        <button
                            onClick={() => setActiveTab('offline')}
                            className={`px-6 py-3 font-semibold transition-all duration-200 border-b-2 ${
                                activeTab === 'offline'
                                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                        >
                            Offline Coupons
                        </button>
                    )}
                    {enabledTabs.marketplace && (
                        <button
                            onClick={() => setActiveTab('marketplace')}
                            className={`px-6 py-3 font-semibold transition-all duration-200 border-b-2 ${
                                activeTab === 'marketplace'
                                    ? 'border-violet-500 text-violet-600 bg-violet-50'
                                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                        >
                            Marketplace Coupons
                        </button>
                    )}
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:space-x-4 relative">
                    {/* Search Bar */}
                    <div className="w-full relative">
                        <input
                            type="text"
                            placeholder="Search Coupons"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="border border-gray-300 rounded-full py-2 pl-11 pr-4 w-full text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm hover:shadow-md transition-shadow duration-300"
                        />
                        <div className="absolute top-1/2 left-4 transform -translate-y-1/2 text-gray-400">
                            <FaSearch className="text-lg" />
                        </div>
                    </div>

                    <div className="flex flex-row w-full md:w-auto max-md:pt-4">
                        {/* Filters Button */}
                        <motion.div
                            key="filters"
                            data-filter-button
                            onClick={(e) => {
                                // Only toggle if clicking the button itself, not the popup
                                if (!showFilterPopup) {
                                    setShowFilterPopup(true);
                                } else {
                                    setShowFilterPopup(false);
                                }
                                if (showSortPopup) setShowSortPopup(false);
                            }}
                            whileHover={{ scale: 1.05 }}
                            className="flex items-center px-4 py-2 rounded-full hover:shadow-md text-sm font-semibold transition cursor-pointer bg-white text-gray-700 border border-gray-300 shadow hover:bg-gray-100 hover:text-gray-900 focus:ring-2 focus:ring-blue-500 relative"
                        >
                            <IoFilter className="mr-2 text-lg" />
                            Filters
                            {showFilterPopup && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 20 }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute top-full -left-20 max-md:left-0 mt-2 w-fit bg-white border border-gray-200 shadow-lg p-4"
                                >
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Expiration Date Range
                                    </label>
                                    <DatePicker
                                        selectsRange
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(update) => {
                                            setDateRange(update);
                                        }}
                                        isClearable={true}
                                        inline
                                        shouldCloseOnSelect={false}
                                    // className="mx-auto"
                                    />
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleClearDateFilter();
                                            setShowFilterPopup(false);
                                        }}
                                        className="mt-2 w-full px-4 py-2 bg-red-400 text-white rounded-md transition-colors duration-300 hover:bg-red-600"
                                    >
                                        Clear Date Filter
                                    </button>
                                </motion.div>
                            )}
                        </motion.div>


                        {/* Sort Button */}
                        <motion.div
                            key="sort"
                            onClick={() => {
                                setShowSortPopup(!showSortPopup);
                                if (showFilterPopup) setShowFilterPopup(false);
                            }}
                            className="flex items-center px-4 py-2 ml-4 rounded-full cursor-pointer hover:shadow-md text-sm font-semibold transition bg-white text-gray-700 border border-gray-300 shadow hover:bg-gray-100 hover:text-gray-900 focus:ring-2 focus:ring-blue-500 relative"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaSort className="mr-2 text-lg" />
                            Sort
                            {showSortPopup && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="absolute top-full right-0 mt-2 w-52 bg-white border border-gray-200 rounded-md shadow-lg"
                                >
                                    {filters.sort && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // prevent triggering the outer onClick
                                                setFilters(prev => {
                                                    const newFilters = { ...prev };
                                                    delete newFilters.sort;
                                                    delete newFilters.sortValue;
                                                    return newFilters;
                                                });
                                                setShowSortPopup(false);
                                            }}
                                            className="w-full text-left px-4 py-2 bg-red-400 text-white ..."
                                        >
                                            Clear Sort
                                        </button>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFilters(prev => ({ ...prev, sort: "id", sortValue: 1 }));
                                            setShowSortPopup(false);
                                        }}
                                        className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "id" && filters.sortValue === 1
                                            ? "bg-blue-500 text-white"
                                            : "hover:bg-gray-100 text-gray-700"
                                            }`}
                                    >
                                        <span>Sort by ID</span>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs opacity-80">(A to Z)</span>
                                            <span className={`${filters.sort === "id" && filters.sortValue === 1 ? 'text-white' : 'text-gray-600'}`}>
                                                ↓
                                            </span>
                                        </div>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFilters(prev => ({ ...prev, sort: "id", sortValue: -1 }));
                                            setShowSortPopup(false);
                                        }}
                                        className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "id" && filters.sortValue === -1
                                            ? "bg-blue-500 text-white"
                                            : "hover:bg-gray-100 text-gray-700"
                                            }`}
                                    >
                                        <span>Sort by ID</span>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs opacity-80">(Z to A)</span>
                                            <span className={`${filters.sort === "id" && filters.sortValue === -1 ? 'text-white' : 'text-gray-600'}`}>
                                                ↑
                                            </span>
                                        </div>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFilters(prev => ({ ...prev, sort: "name", sortValue: 1 }));
                                            setShowSortPopup(false);
                                        }}
                                        className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "name" && filters.sortValue === 1
                                            ? "bg-blue-500 text-white"
                                            : "hover:bg-gray-100 text-gray-700"
                                            }`}
                                    >
                                        <span>Sort by Name</span>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs opacity-80">(A to Z)</span>
                                            <span className={`${filters.sort === "name" && filters.sortValue === 1 ? 'text-white' : 'text-gray-600'}`}>
                                                ↓
                                            </span>
                                        </div>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFilters(prev => ({ ...prev, sort: "name", sortValue: -1 }));
                                            setShowSortPopup(false);
                                        }}
                                        className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "name" && filters.sortValue === -1
                                            ? "bg-blue-500 text-white"
                                            : "hover:bg-gray-100 text-gray-700"
                                            }`}
                                    >
                                        <span>Sort by Name</span>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs opacity-80">(Z to A)</span>
                                            <span className={`${filters.sort === "name" && filters.sortValue === -1 ? 'text-white' : 'text-gray-600'}`}>
                                                ↑
                                            </span>
                                        </div>
                                    </button>
                                </motion.div>
                            )}
                        </motion.div>
                    </div>

                </div>
            </div>

            {loading ? (
                <div className="fixed inset-0 flex items-center justify-center bg-white/80 z-50">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            ) : (
                <div className="overflow-y-auto h-full max-h-[calc(100vh-270px)] md:max-h-[calc(100vh-170px)] px-6 bg-white scrollbar-thin">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 my-3 mb-4">
                        {Array.isArray(coupons) && coupons.length > 0 ? (
                            coupons.map((coupon) => (
                                <motion.div
                                    key={coupon._id}
                                    className="bg-purple-200 relative p-5 coupon-card"
                                >
                                    <div className="container">
                                        <div className="flex justify-between items-center gap-3 mb-3"> {/* Increased margin bottom */}
                                            <div className="flex gap-2 items-center">
                                                <h2 className="text-xl font-semibold text-gray-900 w-9/12 line-clamp-1">
                                                    {coupon.name}
                                                </h2>
                                                {/* Hidden Badge - shown when coupon is hidden from customers */}
                                                {coupon.isHidden && (
                                                    <span className="px-2 py-0.5 text-xs font-bold bg-orange-500 text-white rounded-full">
                                                        Hidden
                                                    </span>
                                                )}
                                                {coupon.isActive && (
                                                    <button
                                                        onClick={() => handleEditCoupon(coupon)}
                                                        className="relative group w-20 text-gray-700 hover:text-gray-900 font-medium text-sm px-4 py-1 border border-gray-400 rounded-full overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
                                                    >
                                                        <span className="absolute inset-0 bg-gradient-to-r from-gray-200 to-gray-400 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out"></span>
                                                        <span className="relative group-hover:text-white">Edit</span>
                                                    </button>
                                                )}
                                            </div>

                                            <div className="relative">
                                                <input
                                                    type="checkbox"
                                                    id={`coupon-toggle-${coupon._id}`}
                                                    checked={coupon.isActive}
                                                    onChange={() => handleToggleCouponStatus(coupon)}
                                                    className="hidden"
                                                />
                                                <label
                                                    htmlFor={`coupon-toggle-${coupon._id}`}
                                                    className={`flex items-center cursor-pointer w-12 h-6 rounded-full p-1 transition-colors duration-300 ease-in-out ${coupon.isActive ? 'bg-green-500' : 'bg-gray-400'}`}
                                                >
                                                    <span
                                                        className={`block w-[1.20rem] h-[1.20rem] bg-white rounded-full shadow-md m-[-2px] transform transition-transform duration-300 ease-in-out ${coupon.isActive ? 'translate-x-6' : 'translate-x-0'}`}
                                                    ></span>
                                                </label>
                                            </div>
                                        </div>
                                        <p className="text-gray-900 text-sm font-bold my-2">
                                            {new Date(coupon.startDate).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                            }).replace(',', '')}
                                            {' - '}
                                            <span style={{ color: new Date(coupon.endDate) < new Date() ? '#ca4b4b' : 'inherit' }}>
                                                {new Date(coupon.endDate).toLocaleDateString('en-GB', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric'
                                                }).replace(',', '')}
                                            </span>
                                        </p>

                                        <p className="text-gray-900 text-sm font-semibold mb-2">
                                            <span className="text-gray-900 text-sm">Discount - </span>
                                            {coupon.discountType === 'fixed'
                                                ? `₹${coupon.discountValue}`
                                                : `${coupon.discountValue}%`}
                                        </p>

                                        <div className="flex items-center justify-between">
                                            {/* Updated description to preserve line breaks */}
                                            <div className="flex flex-col">
                                                {/* Toggle Button */}
                                                <button
                                                    onClick={() =>
                                                        setAccordionOpen(prev => ({
                                                            ...prev,
                                                            [coupon._id]: !prev[coupon._id]
                                                        }))
                                                    }
                                                    className="flex justify-between items-end gap-1 w-full text-gray-700 font-semibold text-sm focus:outline-none"
                                                >
                                                    <span>Description</span>
                                                    {accordionOpen[coupon._id] ? (
                                                        <FaChevronUp />
                                                    ) : (
                                                        <FaChevronDown />
                                                    )}
                                                </button>
                                                {/* Collapsible Description */}
                                                {accordionOpen[coupon._id] && (
                                                    <motion.div
                                                        initial={{ y: -20, opacity: 0 }}
                                                        animate={{ y: 0, opacity: 1 }}
                                                        exit={{ y: -20, opacity: 0 }}
                                                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                                                        className="overflow-hidden mt-2"
                                                    >
                                                        <p className="text-gray-700 text-sm" style={{ whiteSpace: "pre-wrap" }}>
                                                            {coupon.description}
                                                        </p>
                                                    </motion.div>
                                                )}

                                            </div>

                                            {!coupon.isActive && (
                                                <p className="text-red-500 text-sm font-semibold">
                                                    Inactive
                                                </p>
                                            )}
                                        </div>

                                        {/* Delete Button - Bottom Right Corner */}
                                        <div className="flex justify-end mt-3">
                                            <button
                                                onClick={() => handleDeleteRequest(coupon)}
                                                className="relative group text-red-600 hover:text-white font-medium text-sm px-3 py-1.5 border border-red-400 rounded-full overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1.5"
                                                title="Delete Coupon"
                                            >
                                                <span className="absolute inset-0 bg-gradient-to-r from-red-500 to-red-600 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out"></span>
                                                <FaTrashAlt className="relative text-sm" />
                                                <span className="relative text-sm">Delete</span>
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))
                        ) : (
                            <p className="text-gray-600 text-lg">No coupons found</p>
                        )}
                    </div>
                </div>
            )}

            {/* Confirm Delete Modal */}
            <ConfirmModal
                visible={confirmModalVisible}
                message={`Are you sure you want to delete the coupon "${couponToDelete?.name}"? This action cannot be undone.`}
                onConfirm={confirmDeleteCoupon}
                onCancel={cancelDeleteCoupon}
                cancelButtonText="Cancel"
                confirmButtonText="Delete"
            />

            <style>
                {`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 0px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: linear-gradient(to bottom, #4c6ef5, #3b82f6);
                    border-radius: 5px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(to bottom, #3b82f6, #2563eb);
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 5px;
                }
                `}
            </style>
        </div>
    );
};

export default Coupons;